// src/pages/Dashboard/utils/dataTransformers.js
import { safeNum, arrCount, sumBy, getMonthLabel } from "./numberUtils";

/**
 * Initial bucket for a month aggregation row. Kept here so the keys live
 * in one place — adding a new aggregated metric is a one-file change.
 */
const emptyMonthlyRow = (label) => ({
  label,

  totalManHoursWorked: 0,
  totalAvgManpower: 0,
  reportCount: 0,

  ltiCases: 0,
  manDaysLost: 0,
  manHours: 0,

  safetyCommitteeMeetings: 0,
  tbtMeetingHours: 0,
  safetyInductionConducted: 0,

  internalTraining: 0,
  externalTraining: 0,

  internalAudits: 0,
  externalAudits: 0,
  hseAuditInternal: 0,
  hseAuditExternal: 0,

  fatality: 0,
  mtiCases: 0,
  fac: 0,

  nearMissIncidents: 0,
  dangerousOccurrences: 0,
  otherIncidents: 0,

  waterConsumption: 0,
  waterRecycled: 0,
  hazardousWasteGenerated: 0,
  nonHazardousWasteGenerated: 0,

  internalNCRs: 0,
  openNCRs: 0,
  openNCNs: 0,
  uaUcReportedClosed: 0,
});

const accumulateMonthlyRow = (row, r) => {
  row.totalManHoursWorked += safeNum(r.totalManHoursWorked);
  row.totalAvgManpower += safeNum(r.totalAvgManpower);
  row.reportCount += 1;

  row.ltiCases += safeNum(r.ltiCases);
  row.manDaysLost += safeNum(r.manDaysLost);
  row.manHours += safeNum(r.totalManHoursWorked);

  row.safetyCommitteeMeetings += safeNum(r.safetyCommitteeMeetings);
  row.tbtMeetingHours += safeNum(r.tbtMeetingHours);
  row.safetyInductionConducted += safeNum(r.safetyInductionConducted);

  row.internalTraining += arrCount(r.safetyTrainingConductedInternal);
  row.externalTraining += arrCount(r.safetyTrainingConductedExternal);

  row.internalAudits += safeNum(r.internalAudits);
  row.externalAudits += safeNum(r.externalAudits);
  row.hseAuditInternal += safeNum(r.hseAuditInternal);
  row.hseAuditExternal += safeNum(r.hseAuditExternal);

  row.fatality += safeNum(r.fatality);
  row.mtiCases += safeNum(r.mtiCases);
  row.fac += safeNum(r.fac);

  row.nearMissIncidents += safeNum(r.nearMissIncidents);
  row.dangerousOccurrences += safeNum(r.dangerousOccurrences);
  row.otherIncidents += safeNum(r.otherIncidents);

  row.waterConsumption += safeNum(r.waterConsumption);
  row.waterRecycled += safeNum(r.waterRecycled);
  row.hazardousWasteGenerated += safeNum(r.hazardousWasteGenerated);
  row.nonHazardousWasteGenerated += safeNum(r.nonHazardousWasteGenerated);

  row.internalNCRs += safeNum(r.internalNCRs);
  row.openNCRs += arrCount(r.openNCRs);
  row.openNCNs += arrCount(r.openNCNs);
  row.uaUcReportedClosed += safeNum(r.uaUcReportedClosed);
};

const finalizeMonthlyRow = (row) => {
  const fr =
    row.manHours > 0
      ? Number(((row.ltiCases * 1_000_000) / row.manHours).toFixed(2))
      : 0;

  const sr =
    row.manHours > 0
      ? Number(((row.manDaysLost * 1_000_000) / row.manHours).toFixed(2))
      : 0;

  const avgManpower =
    row.reportCount > 0
      ? Number((row.totalAvgManpower / row.reportCount).toFixed(2))
      : 0;

  return { ...row, fr, sr, avgManpower };
};

/**
 * Group raw reports by month-year, producing a flat array of rows ready
 * for recharts.
 */
export const groupMonthly = (reports) => {
  const map = new Map();

  reports.forEach((r) => {
    const label = getMonthLabel(r.createdAt);
    if (!map.has(label)) map.set(label, emptyMonthlyRow(label));
    accumulateMonthlyRow(map.get(label), r);
  });

  return Array.from(map.values()).map(finalizeMonthlyRow);
};

/**
 * Per-project aggregation used for the side-by-side comparison charts.
 */
export const buildComparisonData = (reports) => {
  const map = new Map();

  reports.forEach((r) => {
    const name = r?.project?.projectName || "Unknown";

    if (!map.has(name)) {
      map.set(name, {
        project: name,
        manHours: 0,
        manpower: 0,
        lti: 0,
        nearMiss: 0,
        audits: 0,
      });
    }

    const row = map.get(name);
    row.manHours += safeNum(r.totalManHoursWorked);
    row.manpower += safeNum(r.totalAvgManpower);
    row.lti += safeNum(r.ltiCases);
    row.nearMiss += safeNum(r.nearMissIncidents);
    row.audits +=
      safeNum(r.internalAudits) +
      safeNum(r.externalAudits) +
      safeNum(r.hseAuditInternal) +
      safeNum(r.hseAuditExternal);
  });

  return Array.from(map.values());
};

/**
 * Roll-up totals & derived ratios for the KPI cards.
 */
export const buildTotals = (reports) => {
  const totalManHours = sumBy(reports, "totalManHoursWorked");
  const lti = sumBy(reports, "ltiCases");
  const manDaysLost = sumBy(reports, "manDaysLost");

  return {
    totalManHours,
    avgManpower:
      reports.length > 0
        ? sumBy(reports, "totalAvgManpower") / reports.length
        : 0,
    fatality: sumBy(reports, "fatality"),
    lti,
    nearMiss: sumBy(reports, "nearMissIncidents"),
    fac: sumBy(reports, "fac"),
    fr: totalManHours > 0 ? (lti * 1_000_000) / totalManHours : 0,
    sr: totalManHours > 0 ? (manDaysLost * 1_000_000) / totalManHours : 0,
    audits:
      sumBy(reports, "internalAudits") +
      sumBy(reports, "externalAudits") +
      sumBy(reports, "hseAuditInternal") +
      sumBy(reports, "hseAuditExternal"),
  };
};

/**
 * KPI definitions, ordered as they should appear on the dashboard.
 */
export const buildKpis = (totals) => [
  { label: "Total Man-Hours Worked", value: totals.totalManHours },
  { label: "Average Manpower", value: totals.avgManpower },
  { label: "Fatality Cases", value: totals.fatality },
  { label: "Lost Time Injury Cases (LTI)", value: totals.lti },
  { label: "Frequency Rate (FR)", value: totals.fr },
  { label: "Severity Rate (SR)", value: totals.sr },
  { label: "Near Miss Incidents", value: totals.nearMiss },
  {
    label: "Health, Safety and Environment Audits Done (HSE Audits)",
    value: totals.audits,
  },
];

/**
 * Pie data for the injury breakdown chart.
 */
export const buildInjuryData = (reports) => [
  { name: "Fatality Cases", value: sumBy(reports, "fatality") },
  { name: "Lost Time Injury Cases (LTI)", value: sumBy(reports, "ltiCases") },
  { name: "Medical Treatment Injury Cases (MTI)", value: sumBy(reports, "mtiCases") },
  { name: "First Aid Cases (FAC)", value: sumBy(reports, "fac") },
];

/**
 * Reshape monthly rows for the stacked non-injury area chart.
 */
export const buildNonInjuryData = (monthlyData) =>
  monthlyData.map((d) => ({
    label: d.label,
    "Near Miss Incidents": d.nearMissIncidents,
    "Dangerous Occurrences": d.dangerousOccurrences,
    "Other Incidents": d.otherIncidents,
  }));

/**
 * Unique list of projects discovered across the reports.
 */
export const buildProjectList = (reports) => {
  const map = new Map();

  reports.forEach((r) => {
    const p = r?.project;
    if (!p?._id) return;

    map.set(String(p._id), {
      _id: String(p._id),
      projectName: p.projectName || "Unnamed Project",
      siteLocation: p.siteLocation || "",
      status: p.status || "",
    });
  });

  return Array.from(map.values()).sort((a, b) =>
    a.projectName.localeCompare(b.projectName),
  );
};

/**
 * Unique months that appear in the data set, ready to render in a
 * month picker.
 */
export const buildAvailableMonths = (reports) => {
  const map = new Map();

  reports.forEach((r) => {
    const d = new Date(r.createdAt);
    if (Number.isNaN(d.getTime())) return;

    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const key = `${month}-${year}`;

    if (!map.has(key)) {
      map.set(key, {
        month,
        year,
        label: d.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => a.year - b.year || a.month - b.month,
  );
};
